import React, { useMemo, useEffect, useState } from 'react';
import { useApp } from '../context/AppContext';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useIsMobile from '../hooks/useIsMobile';
import {
  TrendingUp, Package, Zap, FileText, ArrowRight,
  ChevronRight, Clock, BarChart2, Plus
} from 'lucide-react';

const fmt = (n) =>
  n != null ? new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(n) : '—';

const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

const StatusBadge = ({ status }) => {
  const { t } = useTranslation();
  const map = {
    draft:    { label: t('status.draft'),    bg: '#fef9c3', color: '#854d0e' },
    active:   { label: t('status.active'),   bg: '#dcfce7', color: '#166534' },
    archived: { label: t('status.archived'), bg: '#f1f5f9', color: '#64748b' },
  };
  const s = map[status] || map.draft;
  return (
    <span style={{ padding: '2px 10px', borderRadius: 999, fontSize: '0.7rem', fontWeight: 700, background: s.bg, color: s.color }}>
      {s.label}
    </span>
  );
};

export default function Dashboard() {
  const { user, token, fetchDashboard, calculateCapex, saveDiscoveryIntake } = useApp();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [wizardStep, setWizardStep] = useState(1);
  const [wizardSaving, setWizardSaving] = useState(false);
  const [wizardAnswers, setWizardAnswers] = useState({
    payloadDescription: '',
    payloadWeightKg: 20,
    distanceKm: 5,
    flightMinutes: 10,
    environment: 'city',
    overRoads: false,
    constructionSite: false,
    phoneCoverage: 'maybe',
    isOperator: false,
    certifications: [],
    hasOperationalAuthorization: false
  });
  const [wizardDraft, setWizardDraft] = useState(null);

  const load = async () => {
    if (!token) return;
    try {
      setLoading(true); setError(null);
      const d = await fetchDashboard();
      setData(d);
    } catch (e) {
      console.error(e);
      setError(t('dashboard.loadError'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (token) load(); }, [token]);

  const lbsValue = useMemo(() => ((parseFloat(wizardAnswers.payloadWeightKg) || 0) * 2.20462).toFixed(1), [wizardAnswers.payloadWeightKg]);
  const milesValue = useMemo(() => ((parseFloat(wizardAnswers.distanceKm) || 0) * 0.621371).toFixed(1), [wizardAnswers.distanceKm]);

  if (loading) return (
    <div className="page-content" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 300 }}>
      <div style={{ color: 'var(--on-surface-muted)', fontFamily: 'var(--font-head)', fontSize: '1rem' }}>
        {t('dashboard.loading')}
      </div>
    </div>
  );

  if (error) return (
    <div className="page-content">
      <div style={{ color: '#ef4444', padding: '1rem', background: '#fef2f2', borderRadius: 10 }}>
        {error}
        <button onClick={load} style={{ marginLeft: '1rem', padding: '0.25rem 0.75rem', borderRadius: 6, border: '1px solid #ef4444', background: 'transparent', color: '#ef4444', cursor: 'pointer', fontSize: '0.85rem' }}>
          {t('common.retry')}
        </button>
      </div>
    </div>
  );

  if (!data) return null;

  const { stats, configs, scenarios, businessPlans } = data;
  const getScenarioCalcType = (scenario) => {
    const inputParams = scenario?.input_params;
    const inferFromName = () => {
      const n = String(scenario?.name || '').toLowerCase();
      return n.includes('scenario sr') || n.startsWith('sr ') || n.includes(' single route');
    };
    if (typeof inputParams === 'object' && inputParams) {
      if (inputParams.calculatorType) return inputParams.calculatorType;
      if (Object.prototype.hasOwnProperty.call(inputParams, 'payloadKg') && !Object.prototype.hasOwnProperty.call(inputParams, 'totalKg')) {
        return 'single_route';
      }
    }
    if (typeof inputParams === 'string' && inputParams) {
      try {
        const parsed = JSON.parse(inputParams);
        if (parsed?.calculatorType) return parsed.calculatorType;
        if (Object.prototype.hasOwnProperty.call(parsed || {}, 'payloadKg') && !Object.prototype.hasOwnProperty.call(parsed || {}, 'totalKg')) {
          return 'single_route';
        }
      } catch (_) {
        // ignore parse errors and continue with fallback inference
      }
    }
    if (scenario?.scenario_type === 'single_route') return 'single_route';
    if (inferFromName()) return 'single_route';
    return null;
  };

  const statCards = [
    { label: t('dashboard.stats.capex'),     val: stats.totalConfigs,   icon: Package,   color: '#28419a' },
    { label: t('dashboard.stats.opex'),      val: stats.totalScenarios, icon: Zap,       color: '#3a55b8' },
    { label: t('dashboard.stats.bp'),        val: stats.totalBPs,       icon: FileText,  color: '#133fa6' },
    { label: t('dashboard.stats.breakeven'), val: stats.avgBreakeven ? `${stats.avgBreakeven} ${t('common.months')}` : '—', icon: TrendingUp, color: '#059669' },
  ];

  const certificationsCatalog = [
    'A2 Certificate',
    'STS Scenario Training',
    'Aeronautical Comm + CRM',
    'SORA Introduction',
    'FB Piloting Training',
    'FB Maintenance Training'
  ];

  const setAnswer = (k, v) => setWizardAnswers((p) => ({ ...p, [k]: v }));

  const buildDraftFromAnswers = async () => {
    const kg = Math.max(1, parseFloat(wizardAnswers.payloadWeightKg) || 1);
    const distanceKm = Math.max(0.5, parseFloat(wizardAnswers.distanceKm) || 0.5);
    const minutes = Math.max(1, parseFloat(wizardAnswers.flightMinutes) || 1);
    const environmentRisk = ['mountain', 'forest', 'sea'].includes(wizardAnswers.environment) ? 1 : 0;
    const requiresAdvanced = wizardAnswers.overRoads || wizardAnswers.constructionSite || wizardAnswers.phoneCoverage === 'no' || environmentRisk;
    const droneModel = kg > 100 || distanceKm > 20 || minutes > 35 ? 'FB4' : 'FB3';
    const fb3Package = droneModel === 'FB4' ? 'fb3_max_ultra' : (requiresAdvanced ? 'fb3_pro' : 'fb3_base');
    const perTripCapacity = droneModel === 'FB4' ? 200 : 100;
    const trips = Math.ceil(kg / perTripCapacity);
    const missionLoad = (distanceKm / 10) + (minutes / 20) + (trips / 10);
    const numDrones = Math.max(1, Math.min(6, Math.ceil(missionLoad)));
    const batterySetsPerDrone = Math.max(1, Math.min(8, Math.ceil((minutes / 20) + (distanceKm / 15))));
    const totalBatterySets = numDrones * batterySetsPerDrone;
    const totalChargers = Math.max(numDrones, Math.ceil(totalBatterySets / 2));
    const numBatteryExtra = Math.max(0, totalBatterySets - numDrones);
    const numChargersExtra = Math.max(0, totalChargers - numDrones);
    const prefillCapex = {
      droneModel,
      fb3Package,
      numDrones,
      numBattery: numBatteryExtra,
      numChargers: numChargersExtra,
      controllerType: droneModel === 'FB4' ? 'bvlos' : (fb3Package === 'fb3_base' ? 'handheld' : fb3Package === 'fb3_pro' ? 'handheld_pro' : 'bvlos'),
      authorizationSupportLevel: requiresAdvanced ? 'bvlos' : 'vlos',
      mmsPackage: requiresAdvanced ? 'mms_12m' : 'mms_3m',
      supportWarrantyPackage: requiresAdvanced ? 'premium_24m' : 'standard_12m',
      hasStarlink: wizardAnswers.phoneCoverage !== 'yes'
    };

    const capexQuote = await calculateCapex(prefillCapex);
    const dailyOpex = (numDrones * 2 * 0 * 8) + (numDrones * batterySetsPerDrone * 2.5);
    const suggestedDailyPrice = dailyOpex * 1.7;
    const monthlyProfit = (suggestedDailyPrice - dailyOpex) * 22;
    const roiMonths = monthlyProfit > 0 ? Math.ceil((capexQuote.totalCapex || 0) / monthlyProfit) : null;

    return {
      prefillCapex,
      capexQuote,
      opexDraft: {
        useCase: 'middle_mile',
        dailyCost: dailyOpex,
        suggestedDailyPrice
      },
      businessPlanDraft: {
        monthlyProfit,
        roiMonths
      }
    };
  };

  const runWizardDraft = async () => {
    try {
      setWizardSaving(true);
      const draft = await buildDraftFromAnswers();
      setWizardDraft(draft);
      await saveDiscoveryIntake({
        payloadDescription: wizardAnswers.payloadDescription,
        payloadWeightKg: wizardAnswers.payloadWeightKg,
        distanceKm: wizardAnswers.distanceKm,
        flightMinutes: wizardAnswers.flightMinutes,
        environment: wizardAnswers.environment,
        overRoads: wizardAnswers.overRoads,
        constructionSite: wizardAnswers.constructionSite,
        phoneCoverage: wizardAnswers.phoneCoverage,
        isOperator: wizardAnswers.isOperator,
        certifications: wizardAnswers.isOperator ? wizardAnswers.certifications : [],
        hasOperationalAuthorization: wizardAnswers.hasOperationalAuthorization,
        draftCapex: {
          ...draft.prefillCapex,
          totalCapex: draft.capexQuote?.totalCapex || 0,
          packageLabel: draft.capexQuote?.packageLabel || ''
        },
        draftOpex: draft.opexDraft,
        draftBusinessPlan: draft.businessPlanDraft
      });
    } catch (e) {
      console.error(e);
      alert('Errore nel calcolo della bozza. Riprova.');
    } finally {
      setWizardSaving(false);
    }
  };

  return (
    <div className="page-content">

      {/* Header */}
      <div style={{ marginBottom: '2rem' }}>
        <h1 style={{ fontFamily: 'var(--font-head)', fontSize: '1.75rem', fontWeight: 800, color: 'var(--primary)' }}>
          {t('dashboard.welcome', { name: user?.full_name || t('dashboard.defaultName') })} 👋
        </h1>
        <p style={{ color: 'var(--on-surface-muted)', marginTop: '0.25rem' }}>
          {t('dashboard.subtitle')}
        </p>
      </div>

      {/* Discovery wizard disattivato su richiesta */}
      {false && (
      <div className="card" style={{ marginBottom: '2rem' }}>
        <h3 style={{ fontFamily:'var(--font-head)', fontWeight:800, color:'var(--primary)', marginBottom:'0.25rem' }}>
          Trova La Configurazione Adatta A Te
        </h3>
        <p style={{ color:'var(--on-surface-muted)', fontSize:'0.85rem', marginBottom:'1rem' }}>
          Rispondi a poche domande: creiamo una bozza CAPEX, caso d'uso OPEX e business plan iniziale.
        </p>

        <div style={{ display:'flex', gap:'0.5rem', marginBottom:'1rem' }}>
          {[1,2,3,4].map((s) => (
            <div key={s} style={{ flex:1, height:6, borderRadius:999, background: wizardStep >= s ? 'var(--primary)' : 'var(--surface-high)' }} />
          ))}
        </div>

        {wizardStep === 1 && (
          <div style={{ display:'grid', gridTemplateColumns:'2fr 1fr', gap:'1rem' }}>
            <div>
              <label className="label">Cosa vorresti trasportare?</label>
              <input className="input" value={wizardAnswers.payloadDescription} onChange={(e) => setAnswer('payloadDescription', e.target.value)} placeholder="Es. kit medicale, materiale cantiere, ricambi..." />
            </div>
            <div>
              <label className="label">Peso (kg) / lbs</label>
              <input className="input" type="number" min={0} value={wizardAnswers.payloadWeightKg} onChange={(e) => setAnswer('payloadWeightKg', parseFloat(e.target.value) || 0)} />
              <div style={{ fontSize:'0.75rem', color:'var(--on-surface-muted)', marginTop:'0.25rem' }}>{lbsValue} lbs</div>
            </div>
          </div>
        )}

        {wizardStep === 2 && (
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'1rem' }}>
            <div>
              <label className="label">Distanza (km) / miglia: {wizardAnswers.distanceKm} km ({milesValue} mi)</label>
              <input type="range" min={0} max={50} step={0.5} value={wizardAnswers.distanceKm} onChange={(e) => setAnswer('distanceKm', parseFloat(e.target.value) || 0)} style={{ width:'100%' }} />
            </div>
            <div>
              <label className="label">Tempo volo (min): {wizardAnswers.flightMinutes} min</label>
              <input type="range" min={0} max={50} step={1} value={wizardAnswers.flightMinutes} onChange={(e) => setAnswer('flightMinutes', parseFloat(e.target.value) || 0)} style={{ width:'100%' }} />
            </div>
          </div>
        )}

        {wizardStep === 3 && (
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:'1rem' }}>
            <div>
              <label className="label">Dove vuoi volare?</label>
              <select className="input" value={wizardAnswers.environment} onChange={(e) => setAnswer('environment', e.target.value)}>
                <option value="city">Città</option>
                <option value="industrial">Zona industriale</option>
                <option value="countryside">Campagna</option>
                <option value="sea">Mare</option>
                <option value="mountain">Montagna</option>
                <option value="forest">Bosco</option>
                <option value="everywhere">Ovunque</option>
              </select>
            </div>
            <div>
              <label className="label">Passi sopra strade?</label>
              <select className="input" value={wizardAnswers.overRoads ? 'yes' : 'no'} onChange={(e) => setAnswer('overRoads', e.target.value === 'yes')}>
                <option value="no">No</option>
                <option value="yes">Sì</option>
              </select>
            </div>
            <div>
              <label className="label">Area di cantiere?</label>
              <select className="input" value={wizardAnswers.constructionSite ? 'yes' : 'no'} onChange={(e) => setAnswer('constructionSite', e.target.value === 'yes')}>
                <option value="no">No</option>
                <option value="yes">Sì</option>
              </select>
            </div>
            <div>
              <label className="label">Copertura telefonica?</label>
              <select className="input" value={wizardAnswers.phoneCoverage} onChange={(e) => setAnswer('phoneCoverage', e.target.value)}>
                <option value="yes">Sì</option>
                <option value="no">No</option>
                <option value="maybe">Forse</option>
              </select>
            </div>
          </div>
        )}

        {wizardStep === 4 && (
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'1rem' }}>
            <div>
              <label className="label">Sei già operatore?</label>
              <select className="input" value={wizardAnswers.isOperator ? 'yes' : 'no'} onChange={(e) => setAnswer('isOperator', e.target.value === 'yes')}>
                <option value="no">No</option>
                <option value="yes">Sì</option>
              </select>
            </div>
            <div>
              <label className="label">Hai già autorizzazione operativa?</label>
              <select className="input" value={wizardAnswers.hasOperationalAuthorization ? 'yes' : 'no'} onChange={(e) => setAnswer('hasOperationalAuthorization', e.target.value === 'yes')}>
                <option value="no">No</option>
                <option value="yes">Sì</option>
              </select>
            </div>
            {wizardAnswers.isOperator && (
              <div style={{ gridColumn:'1 / -1' }}>
                <label className="label">Che abilitazioni hai?</label>
                <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:'0.5rem' }}>
                  {certificationsCatalog.map((c) => (
                    <label key={c} style={{ display:'flex', alignItems:'center', gap:'0.4rem', fontSize:'0.82rem', padding:'0.5rem', border:'1px solid var(--surface-high)', borderRadius:8 }}>
                      <input
                        type="checkbox"
                        checked={wizardAnswers.certifications.includes(c)}
                        onChange={(e) => {
                          const next = e.target.checked
                            ? [...wizardAnswers.certifications, c]
                            : wizardAnswers.certifications.filter((x) => x !== c);
                          setAnswer('certifications', next);
                        }}
                      />
                      {c}
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        <div style={{ display:'flex', gap:'0.75rem', marginTop:'1rem' }}>
          <button className="btn-secondary" onClick={() => setWizardStep((s) => Math.max(1, s-1))} disabled={wizardStep === 1}>Back</button>
          {wizardStep < 4 ? (
            <button className="btn-primary" onClick={() => setWizardStep((s) => Math.min(4, s+1))}>Next</button>
          ) : (
            <button className="btn-primary" onClick={runWizardDraft} disabled={wizardSaving}>
              {wizardSaving ? 'Calcolo in corso...' : 'Genera Preventivo Bozza'}
            </button>
          )}
        </div>

        {wizardDraft && (
          <div style={{ marginTop:'1rem', borderTop:'1px solid var(--surface-high)', paddingTop:'1rem', display:'grid', gridTemplateColumns:'1.2fr 1fr 1fr', gap:'1rem' }}>
            <div className="stat-card" style={{ padding:'1rem' }}>
              <div style={{ fontSize:'0.72rem', color:'var(--on-surface-muted)', textTransform:'uppercase' }}>Preventivo CAPEX Bozza</div>
              <div style={{ fontFamily:'var(--font-head)', fontSize:'1.6rem', fontWeight:800, color:'var(--primary)' }}>{fmt(wizardDraft.capexQuote?.totalCapex || 0)}</div>
              <div style={{ fontSize:'0.8rem', color:'var(--on-surface-muted)' }}>{wizardDraft.capexQuote?.packageLabel || '—'}</div>
              <button className="btn-primary" style={{ marginTop:'0.75rem' }} onClick={() => navigate('/capex', { state: { prefillCapex: wizardDraft.prefillCapex } })}>
                CAPEX Calculator
              </button>
            </div>
            <div className="stat-card" style={{ padding:'1rem' }}>
              <div style={{ fontSize:'0.72rem', color:'var(--on-surface-muted)', textTransform:'uppercase' }}>Caso D'Uso OPEX</div>
              <div style={{ fontWeight:700, color:'var(--primary)', marginTop:'0.35rem' }}>Middle Mile</div>
              <div style={{ fontSize:'0.85rem', marginTop:'0.35rem' }}>Costo operativo giorno: <strong>{fmt(wizardDraft.opexDraft.dailyCost)}</strong></div>
              <div style={{ fontSize:'0.85rem', marginTop:'0.25rem' }}>Prezzo consigliato giorno: <strong>{fmt(wizardDraft.opexDraft.suggestedDailyPrice)}</strong></div>
            </div>
            <div className="stat-card" style={{ padding:'1rem' }}>
              <div style={{ fontSize:'0.72rem', color:'var(--on-surface-muted)', textTransform:'uppercase' }}>Business Plan Bozza</div>
              <div style={{ fontSize:'0.85rem', marginTop:'0.35rem' }}>Profitto mensile stimato: <strong>{fmt(wizardDraft.businessPlanDraft.monthlyProfit)}</strong></div>
              <div style={{ fontSize:'0.85rem', marginTop:'0.25rem' }}>ROI stimato: <strong>{wizardDraft.businessPlanDraft.roiMonths ? `${wizardDraft.businessPlanDraft.roiMonths} mesi` : 'N/A'}</strong></div>
            </div>
          </div>
        )}
      </div>
      )}

      {/* KPI Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4,1fr)', gap: '1.25rem', marginBottom: '2rem' }}>
        {statCards.map(s => (
          <div key={s.label} className="stat-card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <div style={{ fontSize: '0.7rem', color: 'var(--on-surface-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  {s.label}
                </div>
                <div style={{ fontFamily: 'var(--font-head)', fontSize: '1.75rem', fontWeight: 800, color: s.color, marginTop: '0.25rem' }}>
                  {s.val}
                </div>
              </div>
              <div style={{ width: 40, height: 40, borderRadius: 10, background: `${s.color}18`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <s.icon size={20} color={s.color} />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Quick actions */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '1.25rem', marginBottom: '2.5rem' }}>
        <div className="card">
          <h3 style={{ fontFamily: 'var(--font-head)', fontWeight: 700, marginBottom: '0.5rem' }}>
            {t('dashboard.quickOpex.title')}
          </h3>
          <p style={{ opacity: 0.8, fontSize: '0.875rem', marginBottom: '1.5rem' }}>
            {t('dashboard.quickOpex.desc')}
          </p>
          <button className="btn-secondary" onClick={() => navigate('/opex')} style={{ gap: '0.5rem' }}>
            {t('dashboard.quickOpex.btn')} <ArrowRight size={16} />
          </button>
        </div>
        <div className="card">
          <h3 style={{ fontFamily: 'var(--font-head)', fontWeight: 700, marginBottom: '0.5rem', color: 'var(--primary)' }}>
            {t('dashboard.quickCapex.title')}
          </h3>
          <p style={{ color: 'var(--on-surface-muted)', fontSize: '0.875rem', marginBottom: '1.5rem' }}>
            {t('dashboard.quickCapex.desc')}
          </p>
          <button className="btn-primary" onClick={() => navigate('/capex')} style={{ gap: '0.5rem' }}>
            {t('dashboard.quickCapex.btn')} <ArrowRight size={16} />
          </button>
        </div>
      </div>

      {/* Business Plans Table */}
      <Section
        title={t('dashboard.sections.bp')}
        icon={<BarChart2 size={18} color="#7c3aed" />}
        count={businessPlans.length}
        onAdd={() => navigate('/business-plan')}
        addLabel={t('dashboard.sections.bpAdd')}
        empty={businessPlans.length === 0}
        emptyMsg={t('dashboard.sections.bpEmpty')}
      >
        <table style={tableStyle}>
          <thead>
            <tr>
              {[t('common.name'), t('common.breakeven'), 'ROI 60m', 'CAPEX', 'OPEX/mo', t('common.revenue')+'/mo', t('common.date'), ''].map(h => (
                <th key={h} style={thStyle}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {businessPlans.map((bp, i) => (
              <tr key={bp.id} style={{ background: i % 2 === 0 ? 'transparent' : '#f8fafc' }}>
                <td style={tdStyle}><span style={{ fontWeight: 600, color: 'var(--primary)' }}>{bp.name}</span></td>
                <td style={tdStyle}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <Clock size={13} color="#6b7280" />
                    {bp.breakeven_month ? `${bp.breakeven_month} ${t('common.months')}` : '—'}
                  </span>
                </td>
                <td style={{ ...tdStyle, color: '#059669', fontWeight: 700 }}>
                  {bp.roi_60m != null ? `${(+bp.roi_60m).toFixed(1)}%` : '—'}
                </td>
                <td style={tdStyle}>{fmt(bp.total_capex)}</td>
                <td style={tdStyle}>{fmt(bp.monthly_opex)}</td>
                <td style={{ ...tdStyle, color: '#28419a', fontWeight: 600 }}>{fmt(bp.monthly_revenue)}</td>
                
                <td style={{ ...tdStyle, color: 'var(--on-surface-muted)', fontSize: '0.8rem' }}>{fmtDate(bp.created_at)}</td>
                <td style={tdStyle}>
                  <button onClick={() => navigate('/business-plan', { state: { editId: bp.id } })} style={iconBtn('#7c3aed')} title={t('common.open')}>
                    <ChevronRight size={15} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Section>

      {/* CAPEX Configs Table */}
      <Section
        title={t('dashboard.sections.capex')}
        icon={<Package size={18} color="#28419a" />}
        count={configs.length}
        onAdd={() => navigate('/capex')}
        addLabel={t('dashboard.sections.capexAdd')}
        empty={configs.length === 0}
        emptyMsg={t('dashboard.sections.capexEmpty')}
      >
        <table style={tableStyle}>
          <thead>
            <tr>
              {[t('common.name'), t('common.model'), t('common.drones'), t('dashboard.totalCapex'), t('common.date'), ''].map(h => (
                <th key={h} style={thStyle}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {configs.map((c, i) => (
              <tr key={c.id} style={{ background: i % 2 === 0 ? 'transparent' : '#f8fafc' }}>
                <td style={{ ...tdStyle, fontWeight: 600, color: 'var(--primary)' }}>{c.name}</td>
                <td style={tdStyle}>
                  <span style={{ padding: '2px 10px', borderRadius: 999, fontSize: '0.72rem', fontWeight: 700, background: c.drone_model === 'FB4' ? '#ede9fe' : '#dbeafe', color: c.drone_model === 'FB4' ? '#6d28d9' : '#1d4ed8' }}>
                    {c.drone_model}
                  </span>
                </td>
                <td style={tdStyle}>{c.num_drones}</td>
                <td style={{ ...tdStyle, fontWeight: 700, color: '#28419a' }}>{fmt(c.total_capex)}</td>
                <td style={{ ...tdStyle, color: 'var(--on-surface-muted)', fontSize: '0.8rem' }}>{fmtDate(c.created_at)}</td>
                <td style={tdStyle}>
                  <button onClick={() => navigate('/capex', { state: { editId: c.id } })} style={iconBtn('#28419a')} title={t('common.open')}>
                    <ChevronRight size={15} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Section>

      {/* OPEX Scenarios Table */}
      <Section
        title={t('dashboard.sections.opex')}
        icon={<Zap size={18} color="#3a55b8" />}
        count={scenarios.length}
        onAdd={() => navigate('/opex')}
        addLabel={t('dashboard.sections.opexAdd')}
        empty={scenarios.length === 0}
        emptyMsg={t('dashboard.sections.opexEmpty')}
      >
        <table style={tableStyle}>
          <thead>
            <tr>
              {[t('common.name'), t('common.type'), t('dashboard.dailyCost'), t('dashboard.roiEst'), t('common.date'), ''].map(h => (
                <th key={h} style={thStyle}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {scenarios.map((s, i) => (
              <tr key={s.id} style={{ background: i % 2 === 0 ? 'transparent' : '#f8fafc' }}>
                {(() => {
                  const calcType = getScenarioCalcType(s);
                  const isSingleRoute = calcType === 'single_route';
                  const isMiddleMile = !isSingleRoute && s.scenario_type === 'middle_mile';
                  const badgeStyle = isSingleRoute
                    ? { background: '#e0f2fe', color: '#075985' }   // blue-ish for Single Route
                    : isMiddleMile
                      ? { background: '#fef3c7', color: '#92400e' } // amber for Middle Mile
                      : { background: '#d1fae5', color: '#065f46' };// green for Solar Panel
                  const badgeLabel = isSingleRoute
                    ? '🛣️ Single Route'
                    : (isMiddleMile ? '🚁 Middle Mile' : '☀️ Solar Panel');
                  return (
                    <>
                <td style={{ ...tdStyle, fontWeight: 600, color: 'var(--primary)' }}>{s.name}</td>
                <td style={tdStyle}>
                  <span style={{ padding: '2px 10px', borderRadius: 999, fontSize: '0.72rem', fontWeight: 700, ...badgeStyle }}>
                    {badgeLabel}
                  </span>
                </td>
                <td style={{ ...tdStyle, fontWeight: 700 }}>{fmt(s.daily_cost)}</td>
                <td style={tdStyle}>{s.roi_months ? `${s.roi_months} ${t('common.months')}` : '—'}</td>
                <td style={{ ...tdStyle, color: 'var(--on-surface-muted)', fontSize: '0.8rem' }}>{fmtDate(s.created_at)}</td>
                <td style={tdStyle}>
                  <button onClick={() => navigate('/opex', { state: { editId: s.id } })} style={iconBtn('#3a55b8')} title={t('common.open')}>
                    <ChevronRight size={15} />
                  </button>
                </td>
                    </>
                  );
                })()}
              </tr>
            ))}
          </tbody>
        </table>
      </Section>

    </div>
  );
}

function Section({ title, icon, count, onAdd, addLabel, empty, emptyMsg, children }) {
  return (
    <div className="card" style={{ marginBottom: '1.5rem', padding: 0, overflow: 'hidden' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem 1.25rem', borderBottom: '1px solid var(--surface-high)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {icon}
          <span style={{ fontFamily: 'var(--font-head)', fontWeight: 700, fontSize: '1rem', color: 'var(--primary)' }}>{title}</span>
          <span style={{ padding: '1px 8px', borderRadius: 999, fontSize: '0.7rem', fontWeight: 700, background: '#e0e7ff', color: '#3730a3' }}>{count}</span>
        </div>
        <button className="btn-primary" style={{ padding: '0.35rem 0.9rem', fontSize: '0.8rem', gap: '0.5rem' }} onClick={onAdd}>
          <Plus size={14} /> {addLabel}
        </button>
      </div>
      {empty
        ? <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--on-surface-muted)', fontSize: '0.875rem' }}>{emptyMsg}</div>
        : <div style={{ overflowX: 'auto' }}>{children}</div>
      }
    </div>
  );
}

const tableStyle = { width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' };
const thStyle = { padding: '0.6rem 1rem', textAlign: 'left', fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--on-surface-muted)', background: '#f8fafc', borderBottom: '1px solid var(--surface-high)' };
const tdStyle = { padding: '0.75rem 1rem', borderBottom: '1px solid var(--surface-high)' };
const iconBtn = (color) => ({ background: `${color}12`, border: 'none', borderRadius: 6, padding: '4px 6px', cursor: 'pointer', color, display: 'flex', alignItems: 'center', justifyContent: 'center' });
